/**
 * SUPERVISOR.ACCOUNTS
 * Board of the subscriptions (deals from Monday), list of all accounts, sendings and admins
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { NavigateFunction, useNavigate } from "react-router-dom"
import { store } from "@/index"
import PageHeader from "@/components/page-header"
import Dropdown from "@/components/dropdown"
import NavSupervisor from "@/nav/nav.supervisor"
import { supervisorFetch, supervisorUpdateOptions } from "@/redux/supervisor.actions"
import { Supervisor, SupervisorOptions } from "@/redux/supervisor.types"
import { Session } from "@/redux/_session.types"
import Button from "@/components/button"
import SupervisorAccountConnectModal from "@/modals/supervisor-account-connect.modal"
import {
  accountActivate,
  accountEdit,
  accountGet,
  accountFetchOne_AsSupervisor,
  accountUpdate_AsSupervisor
} from "@/redux/account.actions"
import { ACCOUNT_DEMO, Account, AccountCsType, AccountState } from "@/redux/account.types"
import AccountCsTypeChips from "@/components/account-cs-type-chips"
import AddAccountButton, { initAccount } from "@/widgets/add-account-button.widget"
import AddAccountModal from "@/modals/add-account.modal"
import CardButton from "@/components/card-button"
import CardContainer from "@/components/card-container"
import SupervisorAccountsListWidget from "@/widgets/supervisor-accounts-list.widget"
import AccountSubscriptionsWidget from "@/widgets/account-subscriptions.widget"
import {
  faArrowUpRightFromSquare,
  faChevronLeft,
  faClock,
  faChalkboard,
  faLinkSlash,
  faNoteSticky,
  faPencilAlt,
  faPlay,
  faPlus,
  faSearch
} from "@fortawesome/free-solid-svg-icons"
import env from "@/env"
import PageLoader from "@/components/page-loader"
import Card from "@/components/card"
import ListButton from "@/components/list-button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Space from "@/components/space"
import Modal from "@/components/modal"
import {
  integrationFetchMondayDeals_AsSupervisor,
  integrationIgnoreMondayDeal_AsSupervisor
} from "@/redux/integration.actions"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import { v4 as uuid } from "uuid"
import { Subscription, SubscriptionState } from "@/redux/subscription.types"
import { subscriptionGet, subscriptionUpdate_AsSupervisor } from "@/redux/subscription.actions"
import { getImageUrl } from "@/utils/get-image-url"
import { toast } from "react-toastify"
import TextInput from "@/components/text-input"
import Link from "@/components/link"
import LoadingModal from "@/modals/loading.modal"
import Tabs from "@/components/tabs"
import SupervisorsListWidget from "@/widgets/supervisors-list.widget"
import SupervisorConsultantsListWidget from "@/widgets/supervisor-consultants-list.widget"
import SendingsAllWidget from "@/widgets/sendings-all.widget"
import DeleteUserSuperModal from "@/modals/delete-user-as-supervisor.modal"
import { userActivate } from "@/redux/user.actions"
import { User } from "@/redux/user.types"

interface Props {
  _session: Session
  account: AccountState
  subscription: SubscriptionState
}

//Modals
const MODAL_AUTH_CONFIRM = "MODAL_AUTH_CONFIRM" //Confirm connexion to smartlink
const MODAL_ASSOCIATE = "MODAL_ASSOCIATE" //Choose to create or search an account for a deal
const MODAL_ADD_ACCOUNT = "MODAL_ADD_ACCOUNT" //Create a new account
const MODAL_SEARCH_ACCOUNT = "MODAL_SEARCH_ACCOUNT" //Search an existing account
const MODAL_ACCOUNT_SUBSCRIPTIONS = "MODAL_ACCOUNT_SUBSCRIPTIONS" //Subscriptions of the selected account
const MODAL_HUBSPOT_MISSING = "MODAL_HUBSPOT_MISSING" //No Hubspot link for the deal
const MODAL_CS_TYPE = "MODAL_CS_TYPE" //Type of contract of the account of the deal
const MODAL_DELETE_USER = "MODAL_DELETE_USER" //Display user to delete
const MODAL_NOTE = "MODAL_NOTE" //Note of the subscription of the deal
const IS_NOTE_VISIBLE = false //Note button hidden for now on the cards
const MODAL_DATE = "MODAL_DATE" //Date of the step of the deal
const MODAL_HIDDEN = "MODAL_HIDDEN" //Deals hidden from a column (ignored, old restitutions, archived)
const MODAL_IGNORE = "MODAL_IGNORE" //Confirm to ignore (or activate) the deal
const MODAL_ARCHIVE = "MODAL_ARCHIVE" //Confirm to archive (or activate) the subscription of the deal

//Tabs
const TAB_SUBS = "TAB_SUBS" //Board of the subscriptions (supervisor level 2)
const TAB_MED = "TAB_MED" //All the accounts
const TAB_SEND = "TAB_SEND" //Show upcoming sendings
const TAB_USERS = "TAB_USERS" //Show admins
const TAB_CONSULTANTS = "TAB_CONSULTANTS" //Consulting accounts (number of clients, last client)
const DEFAULT_TABS = [TAB_SUBS, TAB_MED, TAB_SEND, TAB_USERS, TAB_CONSULTANTS] //Order of the tabs, the supervisor can change it (drag and drop)

const ALL_PROJECTS = { id: "all", name: "Afficher tout", firstname: "" }

//Deal from Monday (group is the AE firstname, can be shortened : "Cris" for "Cristian")
interface Deal {
  id: string
  name: string
  group: string
  columns: Record<string, string>
  subscription: Subscription | null
  isIgnored: boolean //Hidden from the board (ex : duplicate in Monday)
}

//Steps of a deal (columns of the board)
//date : date of the subscription set when the step is done
//"Resell à venir" is computed (9 months after dateStart), a deal cannot be moved into it
const STEPS = [
  { id: "NEW", name: "Nouvel abonnement", date: null },
  { id: "SETUP", name: "Cadrage réalisé", date: "dateSetup" },
  { id: "SURVEY", name: "Enquête terminée", date: "dateSurveyEnd" },
  { id: "RESTITUTION", name: "Restitution terminée", date: "dateRestitution" },
  { id: "RESELL", name: "Resell à venir", date: null }
]

//Is the deal owned by the AE (Monday group can be shortened : "Cris" for "Cristian")
function isDealOfSales(deal: Deal, firstname: string): boolean {
  return firstname.startsWith(deal.group)
}

//Get the step of a deal
function getStep(deal: Deal): string {
  const subscription = deal.subscription
  if (!subscription) return "NEW"

  const dateResell = new Date(subscription.dateStart)
  dateResell.setMonth(dateResell.getMonth() + 9)
  if (dateResell <= new Date()) return "RESELL"

  const step = [...STEPS].reverse().find((x) => x.date && subscription[x.date])
  return step ? step.id : "NEW"
}

//Date displayed on the card for each step (field of the subscription, editable on click)
//title : label of the input in the modal
const STEP_DATES: Record<string, { label: string; field: string; title: string }> = {
  NEW: { label: "Signé le", field: "dateStart", title: "Date de signature" },
  SETUP: { label: "Réalisé le", field: "dateSetup", title: "Date de cadrage" },
  SURVEY: { label: "Terminée le", field: "dateSurveyEnd", title: "Date de fin d'enquête" },
  RESTITUTION: { label: "Terminée le", field: "dateRestitution", title: "Date de restitution" },
  RESELL: { label: "Fin le", field: "dateEnd", title: "Date de fin du contrat" }
}

//Date of the step (signature colored by age)
function getStepDate(stepId: string, subscription: Subscription) {
  const stepDate = STEP_DATES[stepId]
  const date: Date | null = stepDate ? subscription[stepDate.field] : null
  if (!date) return null
  return { ...stepDate, date, color: stepId === "NEW" ? getDateColor(date) || "grey-t" : "grey-t" }
}

//Deals hidden from a column, displayed in a modal : link at the bottom of the column, title of the modal
const HIDDEN_STEPS: Record<string, { link: string; title: string }> = {
  NEW: { link: "Afficher les abonnements ignorés", title: "Abonnements ignorés" },
  RESTITUTION: { link: "Afficher les abonnements plus anciens", title: "Restitutions plus anciennes" },
  RESELL: { link: "Afficher les abonnements archivés", title: "Abonnements archivés" }
}

//Restitution done in the last 7 days (the older ones are hidden by default)
function isRestitutionRecent(deal: Deal): boolean {
  const date = deal.subscription?.dateRestitution
  return !!date && Date.now() - new Date(date).getTime() <= 7 * 24 * 60 * 60 * 1000
}

//Color of the signature date : red after 6 months, orange after 3 months
function getDateColor(date: Date): string {
  const months = (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24 * 30)
  if (months > 6) return "red-t"
  if (months > 3) return "orange-t"
  return ""
}

//Place the deal before another one (at the end if not found)
function placeDeal(deals: Deal[], deal: Deal, beforeId: string | null): Deal[] {
  if (deal.id === beforeId) return deals
  const list = deals.filter((x) => x.id !== deal.id)
  const index = list.findIndex((x) => x.id === beforeId)
  list.splice(index === -1 ? list.length : index, 0, deal)
  return list
}

function SupervisorAccounts(props: Props) {
  const { t } = useTranslation()
  const navigate: NavigateFunction = useNavigate()
  const isSubsVisible = props._session.supervisorLevel > 1
  const [tabsOrder, setTabsOrder] = useState<string[]>(DEFAULT_TABS)
  const [myOptions, setMyOptions] = useState<SupervisorOptions>({}) //Preferences of the connected supervisor
  const [currentTab, setCurrentTab] = useState<string>(
    new URLSearchParams(window.location.search).get("tab") || (isSubsVisible ? TAB_SUBS : TAB_MED)
  )
  const [list, setList] = useState<{ id: string; name: string; firstname: string }[]>([ALL_PROJECTS])
  const [active, setActive] = useState<{ id: string; name: string; firstname: string }>(ALL_PROJECTS)
  const [currentModal, setCurrentModal] = useState<string | null>(null)
  const [deals, setDeals] = useState<Deal[]>([])
  const [status, setStatus] = useState<string>(STATUS_LOADING)
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null)
  const [overStepId, setOverStepId] = useState<string | null>(null)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null)
  const [note, setNote] = useState<string>("")
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [dateField, setDateField] = useState<string>("") //Field of the subscription edited in the date modal
  const [dateValue, setDateValue] = useState<string>("") //Date edited (yyyy-mm-dd)
  const [hiddenStepId, setHiddenStepId] = useState<string>("") //Step of the hidden deals displayed in the modal
  const [savingDealId, setSavingDealId] = useState<string | null>(null) //Deal moved (saving its step)

  //Load on start : AE list (supervisors level 1 or 2)
  //Select the connected supervisor if found
  useEffect(() => {
    async function load() {
      const supervisorsResponse: any = await store.dispatch(supervisorFetch(""))

      if (!supervisorsResponse.error) {
        const allSupervisors: Supervisor[] = supervisorsResponse.map((x) => new Supervisor(x))
        const supervisors = allSupervisors.filter((x) => x.isSales)
        setList([ALL_PROJECTS, ...supervisors])
        const me = supervisors.find((x) => x.id === props._session.supervisorId)
        if (me) setActive(me)

        //Order of the tabs saved by the connected supervisor, open the first one (unless a tab is in the url)
        const myself = allSupervisors.find((x) => x.id === props._session.supervisorId)
        if (myself?.options.accountsTabs) {
          const order = getTabsOrder(myself.options.accountsTabs)
          setMyOptions(myself.options)
          setTabsOrder(order)
          if (!new URLSearchParams(window.location.search).get("tab"))
            setCurrentTab(order.find((x) => x !== TAB_SUBS || isSubsVisible) || TAB_MED)
        }
      }
    }

    load()
  }, [])

  //Load the deals from Monday the first time the subscriptions tab is opened
  useEffect(() => {
    async function loadDeals() {
      const dealsResponse: any = await store.dispatch(integrationFetchMondayDeals_AsSupervisor())

      //Deals without subscription first (order kept after, cards are placed where dropped)
      if (!dealsResponse.error)
        setDeals([...dealsResponse].sort((a, b) => Number(a.subscription !== null) - Number(b.subscription !== null)))

      setStatus(STATUS_LOADED)
    }

    if (currentTab === TAB_SUBS && status === STATUS_LOADING) loadDeals()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab])

  //Deals of the selected AE (AE of the linked account if any, else the Monday group)
  const activeDeals =
    active.id === ALL_PROJECTS.id
      ? deals
      : deals.filter((x) =>
          x.subscription?.Account.SupervisorIdSales
            ? x.subscription.Account.SupervisorIdSales === active.id
            : isDealOfSales(x, active.firstname)
        )

  //Get the Hubspot record url of the deal (link set in Monday, can be "Fiche - https://...")
  function getHubspotUrl(deal: Deal): string | undefined {
    return deal.columns.Hubspot?.match(/https?:\/\/\S+/)?.[0]
  }

  //Open the Hubspot record of the deal
  function openHubspot(deal: Deal) {
    const url = getHubspotUrl(deal)
    if (url) window.open(url, "_blank")
    else setCurrentModal(MODAL_HUBSPOT_MISSING)
  }

  //Associate a subscription to a deal
  function associateSubscription(deal: Deal) {
    setSelectedDeal(deal)
    setCurrentModal(MODAL_ASSOCIATE)
  }

  //Values from Monday for a new subscription : amount, dates from the closing (1 year)
  function getSubscriptionValues(deal: Deal): Partial<Subscription> {
    const date = deal.columns["Closing prévu"] ? new Date(deal.columns["Closing prévu"]) : new Date()
    const dateEnd = new Date(date)
    dateEnd.setFullYear(dateEnd.getFullYear() + 1)

    return {
      text: "Accès à la plateforme IDTree pendant un an",
      price: Number(deal.columns.CA) || 0,
      date,
      dateStart: date,
      dateEnd
    }
  }

  //Add a subscription with the values from Monday and select it (saved on link)
  function addSubscription() {
    if (!selectedDeal) return
    const subscription = new Subscription({
      ...getSubscriptionValues(selectedDeal),
      id: uuid(),
      AccountId: store.getState().account.active.id
    })
    store.dispatch(subscriptionGet([subscription, ...props.subscription.list]))
    setSelectedSubscriptionId(subscription.id)
  }

  //Selected subscription (null if deleted)
  const selectedSubscription = props.subscription.list.find((x) => x.id === selectedSubscriptionId)

  //Link the selected subscription to the deal
  async function linkSubscription() {
    const Account = store.getState().account.active
    if (!selectedSubscription || !selectedDeal) return

    const subscription = new Subscription({ ...selectedSubscription, mondayId: selectedDeal.id })
    setIsSaving(true)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setIsSaving(false)
    if (response.error) return

    setDeals((prev) =>
      prev.map((x) =>
        x.id === selectedDeal.id ? { ...x, subscription: new Subscription({ ...subscription, Account }) } : x
      )
    )
    setCurrentModal(null)
  }

  //Select an account > show its subscriptions
  //Select the last subscription (latest start date)
  function selectAccount(account: Account) {
    const last = [...account.Subscriptions].sort(
      (a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime()
    )[0]

    store.dispatch(accountActivate(account))
    store.dispatch(subscriptionGet(account.Subscriptions))
    setSelectedSubscriptionId(last ? last.id : null)
    setCurrentModal(MODAL_ACCOUNT_SUBSCRIPTIONS)
  }

  //Create a new account
  //Name and AE from the deal
  function createAccount() {
    initAccount(props._session)
    if (selectedDeal) {
      const sales = list.find((x) => x.firstname && isDealOfSales(selectedDeal, x.firstname))
      store.dispatch(accountEdit("name", selectedDeal.name))
      if (sales) {
        store.dispatch(accountEdit("SupervisorSales", sales))
        store.dispatch(accountEdit("SupervisorIdSales", sales.id))
      }
    }
    setCurrentModal(MODAL_ADD_ACCOUNT)
  }

  //Drop a deal in a step, before another deal (at the end if null)
  //Set the date of the previous steps (keep it if already set) and clear the next ones
  async function moveDeal(deal: Deal, stepId: string, beforeId: string | null) {
    const index = STEPS.findIndex((x) => x.id === stepId)
    if (stepId === "RESELL") return
    if (stepId === getStep(deal)) return setDeals((prev) => placeDeal(prev, deal, beforeId))
    if (!deal.subscription) return associateSubscription(deal)

    const subscription = new Subscription({ ...deal.subscription })
    STEPS.forEach((step, i) => {
      if (step.date) subscription[step.date] = i <= index ? subscription[step.date] || new Date() : null
    })

    setSavingDealId(deal.id)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setSavingDealId(null)
    if (!response.error) setDeals((prev) => placeDeal(prev, { ...deal, subscription }, beforeId))
  }

  //Get the whole account (the deal only has its id, name and type of contract)
  async function getFullAccount(account: Account): Promise<Account | null> {
    const response: any = await store.dispatch(accountFetchOne_AsSupervisor(account.id))
    if (response && !response.error) return new Account(response)
    if (!response) toast("Compte introuvable", { type: "error" })
    return null
  }

  async function editAccount(account: Account) {
    const fullAccount = await getFullAccount(account)
    if (fullAccount) openAccount(fullAccount)
  }

  //Open the page of the account (with its subscriptions)
  function openAccount(account: Account) {
    store.dispatch(accountActivate(account))
    store.dispatch(subscriptionGet(account.Subscriptions))
    navigate("/supervisor/account?tab=" + currentTab)
  }

  //Delete user
  function deleteUser(user: User) {
    store.dispatch(userActivate(user))
    store.dispatch(accountActivate(user.Account))
    setCurrentModal(MODAL_DELETE_USER)
  }

  //Order of the tabs : the saved ones (still existing) then the missing ones
  function getTabsOrder(tabIds: string[]): string[] {
    const saved = tabIds.filter((x) => DEFAULT_TABS.includes(x))
    return [...saved, ...DEFAULT_TABS.filter((x) => !saved.includes(x))]
  }

  function getTabLabel(tabId: string): string {
    switch (tabId) {
      case TAB_SUBS:
        return t("subscriptions")
      case TAB_MED:
        return t("account_med")
      case TAB_SEND:
        return t("surveys")
      case TAB_CONSULTANTS:
        return "Consultants"
      default:
        return t("user_admins_all")
    }
  }

  //Drag and drop of a tab : save the new order
  function reorderTabs(tabIds: string[]) {
    const order = getTabsOrder(tabIds)
    const options = { ...myOptions, accountsTabs: order }
    setTabsOrder(order)
    setMyOptions(options)
    store.dispatch(supervisorUpdateOptions(options))
  }

  //Select tab
  //Reload account after, reset the AE filter
  function selectTab(tab: string) {
    store.dispatch(accountGet([]))
    setActive(ALL_PROJECTS)
    setCurrentTab(tab)
  }

  //Open the note of the subscription of the deal
  function openNote(deal: Deal) {
    setSelectedDeal(deal)
    setNote(deal.subscription?.note || "")
    setCurrentModal(MODAL_NOTE)
  }

  //Save the note then close
  async function saveNote() {
    const deal = selectedDeal
    if (!deal?.subscription) return

    const subscription = new Subscription({ ...deal.subscription, note })
    setIsSaving(true)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setIsSaving(false)
    if (response.error) return

    setDeals((prev) => prev.map((x) => (x.id === deal.id ? { ...x, subscription } : x)))
    setCurrentModal(null)
  }

  //Open the modal to edit the date of the step
  function openDate(deal: Deal, field: string, date: Date) {
    setSelectedDeal(deal)
    setDateField(field)
    setDateValue(new Date(date).toISOString().split("T")[0])
    setCurrentModal(MODAL_DATE)
  }

  //Save the date then close
  async function saveDate() {
    const deal = selectedDeal
    if (!deal?.subscription || !dateValue) return

    const subscription = new Subscription({ ...deal.subscription, [dateField]: new Date(dateValue) })
    setIsSaving(true)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setIsSaving(false)
    if (response.error) return

    setDeals((prev) => prev.map((x) => (x.id === deal.id ? { ...x, subscription } : x)))
    setCurrentModal(null)
  }

  //Deal displayed in its column : not ignored (only a deal without subscription), recent restitution, resell not archived
  //The others are displayed in a modal (link at the bottom of the column)
  function isDealShown(deal: Deal, stepId: string): boolean {
    if (deal.isIgnored && !deal.subscription) return false
    if (stepId === "RESTITUTION") return isRestitutionRecent(deal)
    if (stepId === "RESELL") return !deal.subscription?.isArchived
    return true
  }

  function getStepDeals(stepId: string): Deal[] {
    return activeDeals.filter((x) => getStep(x) === stepId && isDealShown(x, stepId))
  }

  //Count of the column header : the ignored deals are not counted
  function getStepCount(stepId: string): number {
    return activeDeals.filter((x) => getStep(x) === stepId && !(x.isIgnored && !x.subscription)).length
  }

  function getHiddenDeals(stepId: string): Deal[] {
    return activeDeals.filter((x) => getStep(x) === stepId && !isDealShown(x, stepId))
  }

  //Open the modal of the hidden deals of a step
  function openHidden(stepId: string) {
    setHiddenStepId(stepId)
    setCurrentModal(MODAL_HIDDEN)
  }

  //Card of a deal (in its column or in the modal of the hidden deals)
  function getCard(deal: Deal, stepId: string, i: number) {
    const stepDate = deal.subscription ? getStepDate(stepId, deal.subscription) : null
    //Resell cards can't be dragged : flat with a border
    return (
      <Card
        isWithoutPadding
        className={`p-5 ${stepId === "RESELL" ? "border border-solid border-[#CCCCCC] !shadow-none" : ""}`}>
        <div className="flex items-start gap-2">
          <div>
            <b>{deal.subscription ? deal.subscription.Account.name : deal.name}</b>

            {stepDate && (
              <div
                className={`_hover mt-1 text-sm ${stepDate.color}`}
                onClick={() => openDate(deal, stepDate.field, stepDate.date)}>
                {stepDate.label}{" "}
                <b>
                  {new Date(stepDate.date).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit"
                  })}
                </b>
              </div>
            )}

            {stepId !== "NEW" && stepId !== "SETUP" && deal.subscription && (
              <div
                className="grey-t _hover mt-1 text-sm"
                style={{ color: new Account(deal.subscription.Account).csTypeColor || undefined }}
                onClick={() => openCsType(deal)}>
                {deal.subscription.Account.csType ? (
                  <>
                    Client <b>{t("account_cs_" + deal.subscription.Account.csType).toLowerCase()}</b>
                  </>
                ) : (
                  <>
                    Type : <b>non défini</b>
                  </>
                )}
              </div>
            )}
          </div>

          <Space />

          {/* Logo, and below the link to archive (resell) or ignore (deal not linked to a subscription) */}
          <div className="flex shrink-0 flex-col items-end whitespace-nowrap">
            <div
              className={stepId === "RESELL" ? "h-[40px]" : ""}>
              {deal.subscription && (
                <img
                  src={getImageUrl("Accounts", deal.subscription.Account.id, "")}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                  className="h-[40px] w-[40px] rounded-full object-cover"
                  alt={deal.subscription.Account.name}
                />
              )}
            </div>

            {stepId === "RESELL" && (
              <div className="mt-1 text-sm">
                <Link isWithoutMargin onClick={() => openArchive(deal)}>
                  {deal.subscription?.isArchived ? "Activer" : "Archiver"}
                </Link>
              </div>
            )}

            {stepId === "NEW" && !deal.subscription && (
              <div className="text-sm">
                <Link isWithoutMargin onClick={() => openIgnore(deal)}>
                  {deal.isIgnored ? "Activer" : "Ignorer"}
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          {/* Inactive while the deal is not linked to a subscription */}
          {IS_NOTE_VISIBLE && (
            <ListButton
              icon={faNoteSticky}
              dot={!!deal.subscription?.note}
              isInactive={!deal.subscription}
              text="Note"
              onClick={deal.subscription ? () => openNote(deal) : undefined}
            />
          )}

          <ListButton
            icon={faPencilAlt}
            isInactive={!deal.subscription}
            text={t("account_edit")}
            onClick={deal.subscription ? () => editAccount(deal.subscription!.Account) : undefined}
          />

          {/* Not linked : add a subscription instead of connecting to the account */}
          {deal.subscription ? (
            <ListButton
              icon={faPlay}
              text={t("utils_connect")}
              onClick={() => getAccount(deal.subscription!.Account)}
            />
          ) : (
            <ListButton
              icon={faPlus}
              isFat={i === 0}
              isOutlineColor
              color={i === 0 ? undefined : props._session.accountColors.cta}
              text={t("subscription_add")}
              onClick={() => associateSubscription(deal)}
            />
          )}

          {getHubspotUrl(deal) ? (
            <ListButton
              icon={faArrowUpRightFromSquare}
              text="Ouvrir la fiche Hubspot"
              onClick={() => openHubspot(deal)}
            />
          ) : (
            <ListButton
              icon={faLinkSlash}
              color="#eb5a46"
              text="Fiche Hubspot non définie"
              onClick={() => openHubspot(deal)}
            />
          )}
        </div>
      </Card>
    )
  }

  //Ignore (or activate) : confirm first
  function openIgnore(deal: Deal) {
    setSelectedDeal(deal)
    setCurrentModal(MODAL_IGNORE)
  }

  //Ignore the deal (hidden from the board) or show it again, then close
  async function saveIgnore() {
    const deal = selectedDeal
    if (!deal) return

    setIsSaving(true)
    const response: any = await store.dispatch(integrationIgnoreMondayDeal_AsSupervisor(deal.id, !deal.isIgnored))
    setIsSaving(false)
    if (response.error) return

    setDeals((prev) => prev.map((x) => (x.id === deal.id ? { ...x, isIgnored: !deal.isIgnored } : x)))
    setCurrentModal(null)
  }

  //Archive (or activate) : confirm first
  function openArchive(deal: Deal) {
    setSelectedDeal(deal)
    setCurrentModal(MODAL_ARCHIVE)
  }

  //Archive or activate the subscription then close
  async function saveArchive() {
    const deal = selectedDeal
    if (!deal?.subscription) return

    const subscription = new Subscription({ ...deal.subscription, isArchived: !deal.subscription.isArchived })
    setIsSaving(true)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setIsSaving(false)
    if (response.error) return

    setDeals((prev) => prev.map((x) => (x.id === deal.id ? { ...x, subscription } : x)))
    setCurrentModal(null)
  }

  //Open the modal to set the type of contract of the account of the deal
  function openCsType(deal: Deal) {
    setSelectedDeal(deal)
    setCurrentModal(MODAL_CS_TYPE)
  }

  //Save the type of contract (the whole account is updated : get it from the accounts search)
  //Update every deal of the account
  async function saveCsType(csType: AccountCsType | null) {
    if (!selectedDeal?.subscription) return
    setIsSaving(true)
    const fullAccount = await getFullAccount(selectedDeal.subscription.Account)
    const response: any = fullAccount
      ? await store.dispatch(accountUpdate_AsSupervisor(new Account({ ...fullAccount, csType })))
      : { error: "account_not_found" }
    setIsSaving(false)
    if (!fullAccount || response.error) return

    const accountId = fullAccount.id
    setDeals((prev) =>
      prev.map((x) =>
        x.subscription?.Account.id === accountId
          ? {
              ...x,
              subscription: new Subscription({
                ...x.subscription,
                Account: new Account({ ...x.subscription.Account, csType })
              })
            }
          : x
      )
    )
    setCurrentModal(null)
  }

  //Connect to an account (smartlink account for the demo)
  function getAccount(account: Account) {
    store.dispatch(accountActivate(account))
    setCurrentModal(MODAL_AUTH_CONFIRM)
  }

  return (
    <NavSupervisor>
      {currentModal === MODAL_ASSOCIATE && (
        <Modal isCloseButtonVisible title={t("subscription_add")} onClose={() => setCurrentModal(null)}>
          <CardContainer>
            <CardButton icon={faPlus} title="Créer un nouveau compte" onClick={createAccount} isCta />
            <CardButton
              icon={faSearch}
              title="Rechercher un compte"
              onClick={() => setCurrentModal(MODAL_SEARCH_ACCOUNT)}
            />
          </CardContainer>
        </Modal>
      )}

      {currentModal === MODAL_ADD_ACCOUNT && (
        <AddAccountModal
          hubspotUrl={selectedDeal ? getHubspotUrl(selectedDeal) : undefined}
          onClose={() => setCurrentModal(null)}
          onDone={() => selectAccount(store.getState().account.active)}
        />
      )}

      {currentModal === MODAL_SEARCH_ACCOUNT && (
        <Modal
          isFullScreen
          title="Rechercher un compte"
          onClose={() => setCurrentModal(null)}
          footer={
            <>
              <Button icon={faChevronLeft} onClick={() => setCurrentModal(MODAL_ASSOCIATE)}>
                {t("utils_back")}
              </Button>
              <Button onClick={() => setCurrentModal(null)}>{t("utils_close")}</Button>
            </>
          }>
          <div className="modal-content-list">
            <SupervisorAccountsListWidget isWithoutCard onClick={selectAccount} />
          </div>
        </Modal>
      )}

      {currentModal === MODAL_ACCOUNT_SUBSCRIPTIONS && (
        <Modal
          isFullScreen
          isCloseButtonVisible
          title="Sélectionnez un abonnement"
          onClose={() => setCurrentModal(null)}
          footer={
            <>
              <Button className={selectedSubscription ? "secondary" : "primary"} isWithBorder onClick={addSubscription}>
                {t("subscription_add")}
              </Button>
              {selectedSubscription && (
                <Button className="primary" isLoading={isSaving} onClick={linkSubscription}>
                  {t("utils_next")}
                </Button>
              )}
            </>
          }>
          <AccountSubscriptionsWidget
            isWithoutHeader
            selectedId={selectedSubscriptionId}
            onSelect={(subscription) => setSelectedSubscriptionId(subscription.id)}
          />
        </Modal>
      )}

      {currentModal === MODAL_HUBSPOT_MISSING && (
        <Modal isCloseButtonVisible onClose={() => setCurrentModal(null)}>
          Veuillez définir le lien vers la fiche Hubspot dans Monday.
        </Modal>
      )}

      {currentModal === MODAL_CS_TYPE && selectedDeal?.subscription && (
        <Modal isCloseButtonVisible title="Type de contrat" onClose={() => setCurrentModal(null)}>
          {isSaving && <LoadingModal />}
          <div className="flex h-[44px] items-center">
            <AccountCsTypeChips value={selectedDeal.subscription.Account.csType} onChange={saveCsType} />
          </div>
        </Modal>
      )}

      {currentModal === MODAL_NOTE && selectedDeal?.subscription && (
        <Modal
          disableKeyEvents
          isCloseButtonVisible
          title={"Note (" + selectedDeal.subscription.Account.name + ")"}
          onClose={() => setCurrentModal(null)}
          onNext={saveNote}
          onNextCta={t("utils_save")}
          status={isSaving ? STATUS_SAVING : undefined}>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: 400, height: 120, resize: "none" }}
          />
        </Modal>
      )}

      {currentModal === MODAL_DATE && selectedDeal?.subscription && (
        <Modal
          isCloseButtonVisible
          title="Modifier la date"
          onClose={() => setCurrentModal(null)}
          onNext={saveDate}
          onNextCta={t("utils_save")}
          nextDisabled={!dateValue}
          status={isSaving ? STATUS_SAVING : undefined}>
          <p>
            <b>Compte</b> : {selectedDeal.subscription.Account.name}
          </p>
          <TextInput
            type="date"
            title={Object.values(STEP_DATES).find((x) => x.field === dateField)?.title}
            value={dateValue}
            onChange={(e) => setDateValue(e.value)}
          />
          <div className="h-60" />
        </Modal>
      )}

      {currentModal === MODAL_HIDDEN && HIDDEN_STEPS[hiddenStepId] && (
        <Modal isFullScreen isCloseButtonVisible title={HIDDEN_STEPS[hiddenStepId].title} onClose={() => setCurrentModal(null)}>
          <div className="mt-4 flex flex-wrap gap-4">
            {getHiddenDeals(hiddenStepId).map((deal) => (
              <div key={deal.id} className="w-[300px]">
                {getCard(deal, hiddenStepId, 1)}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {currentModal === MODAL_IGNORE && selectedDeal && (
        <Modal
          isCloseButtonVisible
          title={selectedDeal.isIgnored ? "Activer le deal" : "Ignorer le deal"}
          onClose={() => setCurrentModal(null)}
          onNext={saveIgnore}
          onNextCta={t("utils_confirm")}
          status={isSaving ? STATUS_SAVING : undefined}>
          <p>
            <b>Deal</b> : {selectedDeal.name}
          </p>
          <p className="grey-t">
            {selectedDeal.isIgnored
              ? "Le deal sera de nouveau affiché dans Nouvel abonnement."
              : "Le deal ne sera plus affiché (ex : doublon dans Monday). Vous pourrez le retrouver avec « Afficher les abonnements ignorés »."}
          </p>
        </Modal>
      )}

      {currentModal === MODAL_ARCHIVE && selectedDeal?.subscription && (
        <Modal
          isCloseButtonVisible
          title={selectedDeal.subscription.isArchived ? "Activer l'abonnement" : "Archiver l'abonnement"}
          onClose={() => setCurrentModal(null)}
          onNext={saveArchive}
          onNextCta={t("utils_confirm")}
          status={isSaving ? STATUS_SAVING : undefined}>
          <p>
            <b>Compte</b> : {selectedDeal.subscription.Account.name}
          </p>
          <p className="grey-t">
            {selectedDeal.subscription.isArchived
              ? "L'abonnement sera de nouveau affiché dans Resell à venir."
              : "L'abonnement ne sera plus affiché dans Resell à venir. Vous pourrez le retrouver avec « Afficher les abonnements archivés »."}
          </p>
        </Modal>
      )}

      {currentModal === MODAL_AUTH_CONFIRM && (
        <SupervisorAccountConnectModal account={props.account.active} onClose={() => setCurrentModal(null)} />
      )}

      {currentModal === MODAL_DELETE_USER && <DeleteUserSuperModal onClose={() => setCurrentModal(null)} />}

      <PageHeader title={t("accounts")} subtitle="Suivi des abonnements, des comptes et des enquêtes clients.">
        <div
          className={`relative z-20 flex ${[TAB_SUBS, TAB_MED, TAB_CONSULTANTS].includes(currentTab) ? "" : "invisible"}`}>
          <Dropdown active={active.id} displayField="name" list={list} onSelect={setActive} value={active.name} />
          {active.id !== ALL_PROJECTS.id && (
            <img
              key={active.id}
              src={env.REACT_APP_URL_SPACE.concat("/Supervisors/", active.id, ".png")}
              onError={(e) => (e.currentTarget.style.display = "none")}
              className="my-1 ml-2 h-[44px] w-[44px] rounded-full object-cover"
              alt={active.name}
            />
          )}
        </div>
        <div className="w-[5px]" />
        <AddAccountButton />
        <Button icon={faChalkboard} isWithBorder className="secondary" onClick={() => navigate("/supervisor/demo-prepare")}>
          Préparer démo
        </Button>
        <Button icon={faPlay} isWithBorder className="primary" onClick={() => getAccount(ACCOUNT_DEMO)}>
          Lancer une démo
        </Button>
      </PageHeader>

      <Tabs
        active={currentTab}
        onClick={selectTab}
        onReorder={reorderTabs}
        tabs={Object.fromEntries(
          tabsOrder.filter((x) => x !== TAB_SUBS || isSubsVisible).map((x) => [x, getTabLabel(x)])
        )}
      />

      {currentTab === TAB_MED && (
        <SupervisorAccountsListWidget
          key={active.id}
          salesId={active.id === ALL_PROJECTS.id ? null : active.id}
          onClick={getAccount}
          onEdit={openAccount}
        />
      )}

      {currentTab === TAB_SEND && (
        <Card isWithoutPadding isOverflowHidden>
          <SendingsAllWidget onClick={getAccount} />
        </Card>
      )}

      {currentTab === TAB_USERS && <SupervisorsListWidget onClick={getAccount} onDelete={deleteUser} />}

      {currentTab === TAB_CONSULTANTS && (
        <SupervisorConsultantsListWidget
          onClick={getAccount}
          onEdit={editAccount}
          salesId={active.id === ALL_PROJECTS.id ? null : active.id}
        />
      )}

      {/* Column titles stay on top while scrolling (-20px : over the padding of the scrollable page) */}
      {currentTab === TAB_SUBS && (
        <PageLoader forceLoading status={status}>
          <div className="flex tasks-column-container min-w-[1380px]">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex1 tasks-column medgrey-b min-w-0 ${overStepId === step.id ? "tasks-column-over" : ""}`}
                style={step.id === "RESELL" ? { borderStyle: "dashed" } : undefined}
                onDragOver={(e) => {
                  e.preventDefault()
                  if (step.id !== "RESELL") setOverStepId(step.id)
                }}
                onDrop={() => {
                  setOverStepId(null)
                  if (draggedDeal) moveDeal(draggedDeal, step.id, null)
                }}>
                {step.id === "RESELL" ? (
                  <div className="tasks-column-title lightwhite grey-t sticky top-[-20px] z-10">
                    <FontAwesomeIcon icon={faClock} /> {step.name} ({getStepCount(step.id)})
                  </div>
                ) : (
                  <div className="flex tasks-column-title medgrey white-t sticky top-[-20px] z-10">
                    {step.name} ({getStepCount(step.id)})
                  </div>
                )}

                <div className="tasks-column-card-container">
                  {getStepDeals(step.id).map((deal, i) => {
                    return (
                      <div
                        key={deal.id}
                        draggable={step.id !== "RESELL"}
                        onDragStart={() => setDraggedDeal(deal)}
                        onDrop={(e) => {
                          e.stopPropagation()
                          setOverStepId(null)
                          if (draggedDeal) moveDeal(draggedDeal, step.id, deal.id)
                        }}
                        onDragEnd={() => {
                          setDraggedDeal(null)
                          setOverStepId(null)
                        }}
                        className={`${step.id !== "RESELL" ? "cursor-grab" : ""} ${savingDealId === deal.id ? "opacity-50" : ""}`}>
                        {getCard(deal, step.id, i)}
                      </div>
                    )
                  })}

                  {HIDDEN_STEPS[step.id] && getHiddenDeals(step.id).length > 0 && (
                    <div className="mt-10 pb-4 text-center text-sm">
                      <Link isWithoutMargin onClick={() => openHidden(step.id)}>
                        {HIDDEN_STEPS[step.id].link}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </PageLoader>
      )}
    </NavSupervisor>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account,
  subscription: state.subscription
})

export default connect(mapStateToProps)(SupervisorAccounts)
